<script setup lang="ts">
import '@egjs/vue3-flicking/dist/flicking.css';
import '@egjs/flicking-plugins/dist/pagination.css';

import { AutoPlay, Pagination } from '@egjs/flicking-plugins';
import Flicking, { Plugin } from '@egjs/vue3-flicking';
import { computed, onMounted, ref } from 'vue';

import CaretArrowButton from '@/components/base/CaretArrowButton.vue';
import Header2 from '@/components/base/typography/Header2.vue';

type TypographyStyle = 'Header2' | 'Header3' | 'Header4' | 'BaseBodyText' | 'LargeBodyText';

interface Slide {
  alt?: string;
  content: string;
  typographyStyle: TypographyStyle;
  thumbnail?: string;
}

const props = withDefaults(
  defineProps<{
    autoPlay?: boolean;
    circular?: boolean;
    slides: Slide[];
    slidesPerView?: number;
    showControls?: boolean;
    showPagination?: boolean;
    showThumbnailsOnMobile?: boolean;
  }>(),
  {
    circular: true,
    slidesPerView: 1,
    showControls: true,
    showPagination: true,
  },
);

const carouselContainer = ref<Flicking>();

const plugins: Plugin[] = [];

const prev = () => carouselContainer.value?.prev();
const next = () => carouselContainer.value?.next();
const moveTo = (index: number) => carouselContainer.value?.moveTo(index);
const currentIndex = ref(0);
const carouselLength = computed(() => carouselContainer.value?.panelCount ?? 1);

const thumbnails = computed(() =>
  props.slides
    .map((slide) => {
      if (slide.thumbnail) return { image: slide.thumbnail, alt: slide.alt };
      return null;
    })
    .filter(Boolean),
);

const updateCurrentIndex = (event: { index: number }) => {
  currentIndex.value = event.index;
};

onMounted(() => {
  if (props.autoPlay) {
    plugins.push(new AutoPlay({ duration: 2000 }));
  }
  if (props.showPagination || thumbnails) {
    plugins.push(
      new Pagination({
        type: 'bullet',
      }),
    );
  }
});
</script>

<script lang="ts">
// eslint-disable-next-line import/first
import { BuilderComponent } from '@/utils/cms';

export const CarouselRegistration: BuilderComponent = {
  name: 'Carousel',
  canHaveChildren: true,
  inputs: [
    {
      name: 'autoPlay',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'slides',
      type: 'list',
      defaultValue: [{ content: 'Slide 1 text here', typographyStyle: 'Header2' }],
      subFields: [
        {
          name: 'content',
          type: 'string',
        },
        {
          name: 'typographyStyle',
          type: 'string',
          enum: ['Header2', 'Header3', 'Header4', 'BaseBodyText', 'LargeBodyText'],
        },
        {
          name: 'thumbnail',
          type: 'file',
          helperText: 'Thumbnail images (url) shown below carousel and used as controls',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
        },
        { name: 'alt', type: 'string' },
      ],
    },
    {
      name: 'slidesPerView',
      type: 'number',
      defaultValue: 1,
    },
    {
      name: 'showControls',
      type: 'boolean',
      defaultValue: 'true',
      helperText: 'Show left and right arrows to control carousel',
    },
    {
      name: 'showPagination',
      type: 'boolean',
      defaultValue: 'true',
      helperText: 'Show pagination bullets',
    },
    {
      name: 'showThumbnailsOnMobile',
      type: 'boolean',
      defaultValue: 'false',
      helperText:
        'Show the thumbnail image as part of the slide on mobile (thumbnails are replaced by pagination/dots on mobile)',
    },
  ],
};
</script>

<template>
  <Flicking
    ref="carouselContainer"
    class="min-height-custom"
    :options="{
      align: {
        panel: 'center',
        camera: 'center',
      },
      circular: circular,
      panelsPerView: slidesPerView,
      resizeOnContentsReady: true,
    }"
    :plugins="plugins"
    @changed="updateCurrentIndex"
  >
    <div
      v-for="(slide, index) in slides"
      :aria-hidden="currentIndex !== index"
      :aria-label="`Slide ${index + 1} of ${slides.length}`"
      class="mb-8 text-center"
      :id="`slide-${index}`"
      :key="index"
      role="tabpanel"
    >
      <component :is="Header2">{{ slide.content }}</component>
      <img
        v-if="showThumbnailsOnMobile && slide.thumbnail"
        class="h-5 mt-6 md:hidden"
        :src="slide.thumbnail"
        :alt="`thumbnail-${slide.alt}`"
      />
    </div>
    <template #viewport>
      <CaretArrowButton
        v-if="showControls"
        ariaLabel="Previous slide"
        class="absolute left-0 z-10 text-neutral-500 top-1/2"
        direction="left"
        :disabled="!circular && currentIndex === 0"
        @click="prev"
      />
      <CaretArrowButton
        v-if="showControls"
        ariaLabel="Next slide"
        class="absolute right-0 z-10 text-neutral-500 top-1/2"
        direction="right"
        :disabled="!circular && currentIndex + 1 === carouselLength"
        @click="next"
      />
      <div
        aria-label="Slides"
        class="flicking-pagination"
        :class="{ 'md:hidden': !!thumbnails }"
        role="tablist"
      />
    </template>
  </Flicking>
  <div
    v-if="thumbnails.length"
    class="justify-around hidden mt-8 md:flex"
    role="tablist"
    aria-label="Carousel thumbnails"
  >
    <button
      v-for="(thumbnail, index) in thumbnails"
      :aria-controls="`slide-${index}`"
      :aria-label="`Go to slide ${index + 1}`"
      :aria-selected="currentIndex === index"
      class="h-5 px-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      :class="{ 'opacity-50': index !== currentIndex }"
      :id="`thumbnail-tab-${index}`"
      :key="`thumbnail-${thumbnail?.alt}-${index}`"
      role="tab"
      @click="moveTo(index)"
    >
      <img class="h-full" :src="thumbnail?.image" :alt="`thumbnail-${thumbnail?.alt}`" />
    </button>
  </div>
</template>

<style scoped>
:deep(.flicking-pagination-bullet-active) {
  background-color: black;
}

:deep(.flicking-pagination) {
  bottom: 0;
}

.hidden {
  visibility: visible;
}

.min-height-custom {
  min-height: 120px;
}
</style>
